/**
 * Tracks WHICH API task served each REST call (the `X-Instance-Id` response header) so the browser can
 * prove the multi-task session split that otherwise only shows up as silently dropped broker frames.
 *
 * WHY (2026-07-25 dev incident): the CQG/Rithmic relay sessions live in **per-task memory** on the API
 * (`RithmicSessionManager` holds a `ConcurrentDictionary<userId, session>`), so a session is only usable
 * by the SAME task that created it. Dev runs 2 API tasks. ALB stickiness is enabled, but the SPA's
 * cross-origin `fetch` sends no cookies, so the sticky `AWSALB` cookie is never honoured and requests
 * round-robin. Result: `POST /rithmic/connect` creates the session on task A while the SignalR hub sits
 * on task B → task B has no session → every relayed frame (ResponseLogin, account list, fills) is
 * dropped and the session never leaves `Connecting`.
 *
 * The relays compare their hub's instance id against `lastRestInstanceId()` and shout when they differ.
 */

/** The instance id from the most recent REST response, or null before the first response. */
let lastRest: string | null = null;
/** Every distinct instance id seen this page-load — >1 proves requests are not sticky. */
const seen = new Set<string>();

/** Called by the api client for every response carrying the header. */
export function recordRestInstance(instanceId: string | null, path: string): void {
  if (!instanceId) return;
  const changed = lastRest !== null && lastRest !== instanceId;
  lastRest = instanceId;
  const first = !seen.has(instanceId);
  seen.add(instanceId);

  if (changed || first) {
    // Only log on a CHANGE (or a newly-seen task) — not once per request, which would flood the console.
    const detail = `instance=${instanceId} path=${path} seenThisLoad=[${[...seen].join(',')}]`;
    if (seen.size > 1) {
      console.warn(
        `[api-instance] ⚠ REST calls are being served by MULTIPLE API tasks: ${detail}\n` +
        '  CQG/Rithmic sessions live in per-task memory, so a connect POST and the relay hub landing on ' +
        'different tasks breaks the connect (frames get dropped). Sticky routing is not in effect.',
      );
    } else {
      console.debug(`[api-instance] serving task ${detail}`);
    }
  }
}

/** The task that served the most recent REST call (e.g. the credential-less connect POST). */
export function lastRestInstanceId(): string | null {
  return lastRest;
}

/** All API task ids seen this page-load. More than one ⇒ no session affinity. */
export function seenRestInstanceIds(): string[] {
  return [...seen];
}

/** Test seam — clears the recorded state. */
export function resetRestInstanceTracking(): void {
  lastRest = null;
  seen.clear();
}
